import type { Node } from "@xyflow/react";
import {
  tidyFramesByTimePage,
  type TidyUpdate,
} from "@/components/layout/tidy-frames";
import {
  clearStaleCanvasTimePagePositions,
  rememberStaleCanvasTimePagePositions,
} from "@/lib/canvas-time-page-geometry-guard";
import { useCanvasStore } from "@/stores/canvas-store";
import { withMutationEmitSuppressed } from "@/lib/mirrored-store";
import {
  getCanvasTimePages,
  rememberCanvasTimePageMembership,
} from "@/lib/canvas-time-page-membership";
import type { FrameData } from "@/lib/frame-data";

export interface CanvasTimePageLayoutResult {
  nodes: Node<FrameData>[];
  changedUpdates: TidyUpdate[];
  layoutUpdates: TidyUpdate[];
}

function matchesUpdate(node: Node<FrameData>, update: TidyUpdate) {
  return node.position.x === update.x && node.position.y === update.y;
}

export function applyCanvasTimePageLayout(
  nodes: Node<FrameData>[],
  spaceId: string | null | undefined,
): CanvasTimePageLayoutResult {
  const scopedNodes = nodes.filter(
    (node) => !spaceId || node.data.spaceId === spaceId,
  );
  const updates = tidyFramesByTimePage(scopedNodes, {
    pages: getCanvasTimePages(scopedNodes, spaceId),
  });
  const updateById = new Map(updates.map((update) => [update.id, update]));
  const scopedNodeById = new Map(scopedNodes.map((node) => [node.id, node]));
  const changedUpdates = updates.filter((update) => {
    const node = scopedNodeById.get(update.id);
    return !!node && !matchesUpdate(node, update);
  });
  if (changedUpdates.length === 0) {
    return { nodes, changedUpdates, layoutUpdates: updates };
  }

  return {
    changedUpdates,
    layoutUpdates: updates,
    nodes: nodes.map((node) => {
      const update = updateById.get(node.id);
      if (!update || matchesUpdate(node, update)) return node;
      return {
        ...node,
        position: { x: update.x, y: update.y },
      };
    }),
  };
}

export function ensureCanvasTimePageLayout({
  nodes,
  spaceId,
}: {
  nodes: Node<FrameData>[];
  spaceId?: string;
}): Node<FrameData>[] {
  // React effects can still hold the render before a frame was added, removed,
  // or updated. Derived layout must never replace current canvas membership.
  nodes = useCanvasStore.getState().nodes;
  const result = applyCanvasTimePageLayout(nodes, spaceId);
  rememberCanvasTimePageMembership(result.nodes, spaceId);
  const scopedFrameIds = nodes
    .filter((node) => !spaceId || node.data.spaceId === spaceId)
    .map((node) => node.id);
  if (result.layoutUpdates.length === 0) {
    clearStaleCanvasTimePagePositions(scopedFrameIds);
  } else if (result.changedUpdates.length > 0) {
    const nodeById = new Map(nodes.map((node) => [node.id, node]));
    rememberStaleCanvasTimePagePositions(
      result.changedUpdates.flatMap((update) => {
        const node = nodeById.get(update.id);
        return node
          ? [{ id: update.id, x: node.position.x, y: node.position.y }]
          : [];
      }),
    );
  }
  if (result.changedUpdates.length === 0) return nodes;

  // Every display derives this geometry itself. Broadcasting a whole node
  // array from a background display can overwrite newer canonical frames.
  withMutationEmitSuppressed(() => {
    useCanvasStore.getState().setNodes(result.nodes);
  });
  return result.nodes;
}
